import React, { useEffect, useState } from 'react';
import AddPlainDesign from './add-plain-design';
import PageHeaderText from 'components/base/page-header-text';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import { PlainDesignType } from 'utils/types';
import { getSimpleDateFormat } from 'utils/functions';
import { DeleteIcon } from 'cokitchen-icons';
import InfiniteScroll from 'react-infinite-scroll-component';
import DeleteModal from 'components/base/delete-modal';
import PlainDesignEmptyState from './empty-state';
import useDashboardContext from 'contexts/dashboard.context';
import AccessDenied from 'components/access-denied';

const PlainDesigns = () => {
  const [showAddPlainDesign, setShowAddPlainDesign] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [plainDesigns, setPlainDesigns] = useState<PlainDesignType[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [deletePlainDesign, setDeletePlainDesign] = useState(false);
  const [selectedDesignId, setSelectedDesignId] = useState('');
  const { permissions } = useDashboardContext();

  const fetchPlainDesigns = async () => {
    try {
      setLoading(true);
      const res = await apiService.get(`vouchers/plain-cards`, {
        params: { page: page.toString() },
        isInternal: true,
        baseURL: process.env.REACT_APP_ORDER_API
      });
      if (res?.data) {
        setTotalPages(res?.data?.data?.pagination?.totalPages);
        const moreData = res?.data?.data?.data;
        if (page === 1) {
          setPlainDesigns(moreData);
        } else {
          setPlainDesigns([...plainDesigns, ...moreData]);
        }
      }
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err?.response?.data?.message} />);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlainDesigns();
  }, []);

  const handleDelete = async () => {
    try {
      setDeleteLoading(true);
      await apiService.delete(`vouchers/plain-cards/${selectedDesignId}`, {
        isInternal: true,
        baseURL: process.env.REACT_APP_ORDER_API
      });
      toast(<ToastMessage type='success' message='Plain design deleted successfully' />);
      const newPlainDesigns = plainDesigns.filter((item) => item.id !== selectedDesignId);
      setPlainDesigns(newPlainDesigns);
    } catch (err: any) {
      toast(<ToastMessage type='error' message={err?.response?.data?.message} />);
    } finally {
      setDeletePlainDesign(false);
      setDeleteLoading(false);
    }
  };
  useEffect(() => {
    if (page > 1) {
      fetchPlainDesigns();
      setHasMore(page < totalPages);
    }
  }, [page]);

  const fetchMoreData = () => {
    setPage((p) => p + 1);
  };
  const canAccessGiftCards = permissions.canAccessGiftCards;
  if (!canAccessGiftCards) return <AccessDenied />;
  return (
    <div className='max-w-[960px]'>
      <PageHeaderText
        title='Manage Plain designs'
        description={
          plainDesigns?.length === 0
            ? 'Create and manage gift card categories'
            : 'Give your users a variety to pick from by creating and managing a library of plain designs'
        }>
        <button
          onClick={() => setShowAddPlainDesign(true)}
          className='cursor-pointer text-sm font-medium text-[#E60A2B] w-fit'>
          + Add image
        </button>
      </PageHeaderText>

      {loading ? (
        <p className='flex justify-center items-center min-h-full mt-20'>Loading...</p>
      ) : plainDesigns?.length === 0 ? (
        <PlainDesignEmptyState addPlainDesign={() => setShowAddPlainDesign(true)} />
      ) : (
        <InfiniteScroll
          dataLength={plainDesigns?.length}
          next={() => fetchMoreData()}
          hasMore={hasMore}
          loader={loading}
          height='80vh'
          endMessage={
            <p style={{ textAlign: 'center' }}>
              <b>Yay! You have seen it all</b>
            </p>
          }>
          <div className='grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 gap-6'>
            {plainDesigns?.map((item) => (
              <div key={item?.id}>
                <div className=' relative group'>
                  <img
                    src={item?.image_url}
                    alt='plain-designs'
                    className='rounded-sm w-full sm:w-[222px] h-[160px] object-cover '
                  />
                  <div
                    onClick={() => {
                      setSelectedDesignId(item.id);
                      setDeletePlainDesign(true);
                    }}
                    className='absolute bottom-[16px] w-[30px] h-[30px] rounded-full bg-[#fff] flex items-center justify-center left-5 cursor-pointer opacity-0 group-hover:opacity-100 '>
                    <DeleteIcon className='text-[#1A1A1A]' />
                  </div>
                </div>
                <h3 className='text-sm font-medium my-2'>{item?.name ?? 'Plain Card'}</h3>
                <p className='text-[#858585] text-xs'>
                  Added {getSimpleDateFormat(item?.updated_at ?? '')}
                </p>
              </div>
            ))}
          </div>
        </InfiniteScroll>
      )}

      <AddPlainDesign
        onRefetch={fetchPlainDesigns}
        isOpen={showAddPlainDesign}
        onClose={() => setShowAddPlainDesign(false)}
      />
      <DeleteModal
        title={`Are you sure you want to delete ${
          plainDesigns?.find((item) => item.id === selectedDesignId)?.name ?? ''
        } ?`}
        description='This action cannot be undone'
        show={deletePlainDesign}
        onClose={() => setDeletePlainDesign(false)}
        handleDelete={handleDelete}
        loading={deleteLoading}
        type='delete'
      />
    </div>
  );
};

export default PlainDesigns;
